import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFinancialStore } from '../store/useFinancialStore';
import { apiClient } from '../lib/api';
import GlassCard from '../components/GlassCard';
import GlassButton from '../components/GlassButton';

export default function AuthLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refreshDashboard } = useFinancialStore();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.login(email, password);
      if (response.data) {
        localStorage.setItem('auth_token', (response.data as any).access_token);
        await refreshDashboard();
        navigate('/dashboard');
      } else {
        setError(response.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Connection refused by authentication gateway.');
      // Fallback for demo
      if (import.meta.env.DEV) {
         setTimeout(() => {
           navigate('/dashboard');
         }, 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(45,212,191,0.05),transparent_50%)] pointer-events-none" />
      
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl tabular-nums tracking-tighter font-bold text-text-primary mb-2">Initialize Terminal</h1>
          <p className="text-text-secondary text-sm font-bold uppercase tracking-widest opacity-60">Access the RICHGURU Sovereign Trading Network</p>
        </div>

        <GlassCard variant="elevated" className="p-8 border-white/5 relative overflow-hidden group">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-brand-teal/10 blur-[80px] rounded-full group-hover:bg-brand-teal/20 transition-all duration-700" />
          
          <form onSubmit={handleLogin} className="space-y-6 relative z-10">
            {error && (
              <div className="p-4 bg-brand-plum/10 border border-brand-plum/20 rounded-2xl text-brand-plum-light text-[10px] font-black uppercase tracking-widest text-center shadow-glow">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-text-tertiary pl-1">Command Identity</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-4 text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal/30 transition-all backdrop-blur-3xl font-medium"
                placeholder="operator@richguru.ai"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-text-tertiary pl-1">Access Key</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-4 text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal/30 transition-all backdrop-blur-3xl font-medium"
                placeholder="••••••••"
                required
              />
            </div>

            <div className="pt-4">
              <GlassButton
                type="submit"
                variant="primary"
                size="lg"
                glowEffect
                className="w-full h-16 rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-glow"
                disabled={isLoading}
              >
                {isLoading ? 'Decrypting Access...' : 'Gain Access'}
              </GlassButton>
            </div>
          </form>

          <div className="mt-10 pt-8 border-t border-white/5 text-center">
            <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest">
              New operative? {' '}
              <Link to="/auth/register" className="text-brand-teal-light font-black hover:text-white transition-colors">
                Establish Protocol
              </Link>
            </p>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
